import React, { useState, useEffect } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import moment from "moment";
import { API_URL } from "../../../../constants/index";
import { PostServiceCaller } from "../../../../services/serviceCaller";
import { toast } from "react-toastify";

const ApproveWfhModal = ({ show, onHide, groupedRequests, onSuccess }) => {
  const [pendingActions, setPendingActions] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal is opened/closed
  useEffect(() => {
    if (show) {
      setPendingActions({});
      setIsSubmitting(false);
    }
  }, [show]);

  if (!groupedRequests || groupedRequests.length === 0) return null;

  const handleActionSelect = (reqId, actionType) => {
    setPendingActions(prev => ({ ...prev, [reqId]: actionType }));
  };

  const pendingRequests = groupedRequests.filter(req => req.status === 824);

  const isAllApproved = () => {
    if (pendingRequests.length === 0) return false;
    return pendingRequests.every(req => pendingActions[req.id] === 826);
  };

  const isAllRejected = () => {
    if (pendingRequests.length === 0) return false;
    return pendingRequests.every(req => pendingActions[req.id] === 827);
  };

  const handleToggleAll = (targetStatus, isChecked) => {
    const newActions = { ...pendingActions };
    pendingRequests.forEach(req => {
      if (isChecked) {
        newActions[req.id] = targetStatus;
      } else if (newActions[req.id] === targetStatus) {
        delete newActions[req.id];
      }
    });
    setPendingActions(newActions);
  };

  const handleApproveAll = (e) => handleToggleAll(826, e.target.checked);
  const handleRejectAll = (e) => handleToggleAll(827, e.target.checked);

  const handleSubmit = async () => {
    const actionsToProcess = Object.entries(pendingActions);
    if (actionsToProcess.length === 0) {
      toast.info("No actions selected.");
      return;
    }

    setIsSubmitting(true);

    const actionsPayload = actionsToProcess.map(([reqId, actionType]) => ({
      request_id: Number(reqId),
      status: actionType,
      remarks: actionType === 826 ? "Approved by Manager" : "Rejected by Manager"
    }));

    try {
      const response = await PostServiceCaller(
        `${API_URL}manager/work-requests/approve`,
        { actions: actionsPayload },
        "post"
      );

      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(`Successfully processed ${actionsPayload.length} request(s).`);
        onSuccess();
        onHide();
      } else {
        toast.error("Failed to process requests. Please try again.");
      }
    } catch (error) {
      console.error("Error processing requests:", error);
      toast.error("An error occurred while processing requests.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">Approve WFH/BT Request</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Table bordered hover responsive className="mb-0 text-center" style={{ fontSize: "14px" }}>
          <thead id="thcolor">
            <tr>
              <th className="align-middle">Sr. No. <i className="fa fa-angle-down ms-1"></i></th>
              <th className="align-middle">Date <i className="fa fa-angle-down ms-1"></i></th>
              <th className="align-middle">Type <i className="fa fa-angle-down ms-1"></i></th>
              <th className="align-middle">Status <i className="fa fa-angle-down ms-1"></i></th>
              <th className="align-middle" style={{ minWidth: "220px" }}>
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <label className="d-flex align-items-center gap-1 mb-0" style={{ cursor: "pointer" }}>
                    <input 
                      type="checkbox" 
                      className="form-check-input mt-0" 
                      title="Approve All Pending"
                      onChange={handleApproveAll}
                      checked={isAllApproved()}
                      disabled={pendingRequests.length === 0}
                    />
                    <span>Approve All</span>
                  </label>
                  <label className="d-flex align-items-center gap-1 mb-0" style={{ cursor: "pointer" }}>
                    <input 
                      type="checkbox" 
                      className="form-check-input mt-0" 
                      title="Reject All Pending"
                      onChange={handleRejectAll}
                      checked={isAllRejected()}
                      disabled={pendingRequests.length === 0}
                    />
                    <span>Reject All</span>
                  </label>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {groupedRequests.map((req, idx) => {
              const currentStatusName = req.status === 824 
                ? (pendingActions[req.id] === 826 ? "To be Approved" : (pendingActions[req.id] === 827 ? "To be Rejected" : req.status_name))
                : req.status_name;

              return (
                <tr key={idx}>
                  <td className="align-middle">{idx + 1}</td>
                  <td className="align-middle fw-bold">
                    {moment(req.date || req.from_date).format("DD-MMM-YYYY")}
                  </td>
                  <td className="align-middle">{req.request_type}</td>
                  <td className="align-middle">
                    <span className={pendingActions[req.id] ? "text-primary fw-bold" : ""}>
                      {currentStatusName}
                    </span>
                  </td>
                  <td className="align-middle">
                    {req.status === 824 ? (
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className={`btn btn-sm py-0 px-2 ${pendingActions[req.id] === 826 ? 'btn-success' : 'btn-outline-success'}`}
                          onClick={() => handleActionSelect(req.id, 826)}
                          disabled={isSubmitting}
                          title="Approve"
                        >
                          <i className="fa fa-check"></i>
                        </button>
                        <button
                          className={`btn btn-sm py-0 px-2 ${pendingActions[req.id] === 827 ? 'btn-danger' : 'btn-outline-danger'}`}
                          onClick={() => handleActionSelect(req.id, 827)}
                          disabled={isSubmitting}
                          title="Reject"
                        >
                          <i className="fa fa-close"></i>
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={isSubmitting}>
          Close
        </Button>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={handleSubmit} 
          disabled={isSubmitting || Object.keys(pendingActions).length === 0}
          style={{ backgroundColor: "#2b5687", borderColor: "#2b5687" }}
        >
          {isSubmitting ? (
            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Submitting...</>
          ) : (
            "Submit"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApproveWfhModal;
